// client/src/components/SearchKeywords.js — a mandate's search keywords
// (migrations/086, src/lib/mandateKeywords.js): the words that bring it up in
// global search ("credit card" for a payments company). Claude writes them
// from the mandate's facts and materials; RGA edits or regenerates them here.
// Staff only; the portal never shows them.
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { Modal, ErrorBanner } from './people/ui'

const linkBtn = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
  fontSize: 10.5, color: 'var(--accent)', letterSpacing: '0.06em', textTransform: 'uppercase',
}

export function useSearchKeywords(mandateId) {
  return useQuery({
    queryKey: ['search-keywords', mandateId],
    queryFn: () => api.get(`/api/mandates/${mandateId}/search-keywords`).then(r => r.data.data),
  })
}

// The box label: title plus Edit and Regenerate.
export function SearchKeywordsLabel({ mandateId, onEdit }) {
  const qc = useQueryClient()
  const regen = useMutation({
    mutationFn: () => api.post(`/api/mandates/${mandateId}/search-keywords/generate`).then(r => r.data.data),
    onSuccess: data => qc.setQueryData(['search-keywords', mandateId], data),
  })
  return (
    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
      Search Keywords
      <span style={{ display: 'flex', gap: 12 }}>
        <button type="button" style={linkBtn} disabled={regen.isPending}
          title="Rewrite the list from the mandate's current details and materials"
          onClick={() => {
            if (window.confirm('Replace these keywords with a fresh list written from the mandate\'s current details and materials?')) regen.mutate()
          }}>
          {regen.isPending ? 'Writing…' : 'Regenerate'}
        </button>
        <button type="button" style={linkBtn} onClick={onEdit}>Edit</button>
      </span>
      {regen.error && (
        <span style={{ fontSize: 11, color: 'var(--danger-text)' }}>{regen.error.response?.data?.error ?? regen.error.message}</span>
      )}
    </span>
  )
}

export function SearchKeywordChips({ data }) {
  if (!data?.keywords?.length) {
    return <span style={{ color: 'var(--text-muted)' }}>None yet. Regenerate to write them.</span>
  }
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {data.keywords.map(k => <span key={k} className="badge badge-gray" style={{ fontSize: 10 }}>{k}</span>)}
    </div>
  )
}

// One keyword per line (commas also split). Capitals, hyphens and plurals
// don't matter to search, so there's no need to list variants.
export function EditSearchKeywordsModal({ mandateId, keywords, onClose }) {
  const qc = useQueryClient()
  const [text, setText] = useState((keywords ?? []).join('\n'))
  const save = useMutation({
    mutationFn: () => api.put(`/api/mandates/${mandateId}/search-keywords`, {
      keywords: text.split(/[\n,]/).map(s => s.trim()).filter(Boolean),
    }).then(r => r.data.data),
    onSuccess: data => {
      qc.setQueryData(['search-keywords', mandateId], data)
      onClose()
    },
  })
  return (
    <Modal title="Edit Search Keywords" onClose={onClose} width={520}>
      <form onSubmit={e => { e.preventDefault(); save.mutate() }} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          One keyword or short phrase per line. Searching any of these brings up this mandate. Capitals, hyphens and plurals don't matter, so there is no need to list variants.
        </div>
        <textarea className="input" rows={14} value={text} onChange={e => setText(e.target.value)}
          style={{ fontFamily: 'inherit', resize: 'vertical' }} />
        <ErrorBanner msg={save.error?.response?.data?.error ?? save.error?.message} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  )
}
